import React, { useState } from "react";
import { motion } from "framer-motion";
import useTechCopy from "../useTechCopy";
import SectionHeader from "../ui/SectionHeader";
import ShinyText from "../ui/ShinyText";

// The four disciplines set as hero-scale words. The selected word carries the
// headline's shine; the rest sit dim. One panel on the right shows what we
// build for the selected discipline. Tabs pattern: arrow keys move through
// the list, and the panel is labelled by the selected word.
export default function CapabilitiesSection() {
  const copy = useTechCopy().capabilities;
  const items = copy.items;
  const [activeKey, setActiveKey] = useState(items[0].key);
  const active = items.find((it) => it.key === activeKey) || items[0];

  const moveBy = (fromKey, delta) => {
    const i = items.findIndex((it) => it.key === fromKey);
    const next = items[(i + delta + items.length) % items.length];
    setActiveKey(next.key);
    document.getElementById(`cap-tab-${next.key}`)?.focus();
  };

  const onKeyDown = (e, key) => {
    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      e.preventDefault();
      moveBy(key, 1);
    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      e.preventDefault();
      moveBy(key, -1);
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveKey(items[0].key);
    } else if (e.key === "End") {
      e.preventDefault();
      setActiveKey(items[items.length - 1].key);
    }
  };

  return (
    <section id="capabilities" className="relative bg-black px-6 sm:px-10 lg:px-16 py-24 lg:py-32">
      <div className="max-w-[1600px] mx-auto">
        <SectionHeader title={copy.title} lede={copy.lede} />

        <div className="mt-14 lg:mt-24 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div
            role="tablist"
            aria-label={copy.listLabel}
            aria-orientation="vertical"
            className="lg:col-span-7 flex flex-col items-start"
          >
            {items.map((it) => {
              const selected = it.key === activeKey;
              return (
                <button
                  key={it.key}
                  type="button"
                  role="tab"
                  id={`cap-tab-${it.key}`}
                  aria-selected={selected}
                  aria-controls="cap-panel"
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setActiveKey(it.key)}
                  onKeyDown={(e) => onKeyDown(e, it.key)}
                  className="group block text-left py-1 font-medium tracking-tighter leading-[0.95] text-[clamp(3rem,8vw,7.5rem)] rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-8 focus-visible:outline-white/60"
                >
                  {selected ? (
                    <ShinyText text={it.word} className="block py-[0.15em] -my-[0.15em]" />
                  ) : (
                    <span className="block text-white/25 group-hover:text-white/60 transition-colors duration-300">
                      {it.word}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div
            id="cap-panel"
            role="tabpanel"
            aria-labelledby={`cap-tab-${active.key}`}
            className="lg:col-span-5 lg:sticky lg:top-28 rounded-3xl border border-gray-700 p-7 sm:p-9 lg:p-10"
          >
            <motion.div
              key={active.key}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              <h3 className="text-2xl sm:text-3xl font-medium tracking-tight text-white">{active.title}</h3>
              <p className="mt-4 text-white/80 leading-relaxed">{active.desc}</p>
              <dl className="mt-8 space-y-6">
                <div>
                  <dt className="text-sm text-white/50">{copy.builtWith}</dt>
                  <dd className="mt-1 text-white">{active.stack.join(", ")}</dd>
                </div>
                <div>
                  <dt className="text-sm text-white/50">{copy.youGet}</dt>
                  <dd className="mt-1 text-white">{active.deliverable}</dd>
                </div>
              </dl>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
